import { useCallback, useEffect, useState } from "react";
import { useGlobalReducer } from "../../../GlobalContext";
import { Search } from "../../api/searchService";
import { isFailedResponse } from "../../api/request";
import {
  MarketingListMember,
  MarketingListMembersInput,
  MarketingListMembersResult,
} from "./useMarketingListMembers.types";

/**
 * One page of a marketing list's members, searched on the server. A list can hold
 * hundreds of thousands of contacts, so members are never loaded together: the name,
 * email, ordering and page all go to the service, and each change is a fresh read.
 */
export function useMarketingListMembers({
  listId,
  name,
  email,
  sortKey,
  sortAscending,
  page,
  pageSize,
  searchToken,
}: MarketingListMembersInput): MarketingListMembersResult {
  const globalDispatch = useGlobalReducer();
  const [members, setMembers] = useState<MarketingListMember[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [readCount, setReadCount] = useState(0);

  useEffect(() => {
    if (!listId) {
      return undefined;
    }

    let cancelled = false;

    const read = async () => {
      setLoading(true);

      const response = await Search(globalDispatch, "", {
        EntityName: "listmember",
        Fields: null,
        Filters: {
          parentId: listId,
          fullname: name,
          emailaddress1: email,
          fieldOrderedBy: sortKey,
          fieldOrderedByAsc: sortAscending,
          fieldOrderedBySecondary: "",
          fieldOrderedBySecondaryAsc: false,
        },
        IsAdvancedSearch: false,
        AdvancedSearchJoins: [],
        AdvancedSearchNodes: null,
        PageRequested: page,
        PageSize: pageSize,
        IsExport: undefined,
        MarketingListId: undefined,
        MarketingListName: undefined,
        RemoveFromMarketingList: undefined,
        ReplaceExistingListMembers: undefined,
      });

      if (cancelled) {
        return;
      }

      const data = isFailedResponse(response) ? undefined : response?.data;

      setMembers(data?.TotalRecords > 0 ? (data.Records ?? []) : []);
      setTotal(data?.TotalRecords ?? 0);
      setLoading(false);
    };

    read();

    return () => {
      cancelled = true;
    };
    // searchToken is what re-reads when the filters themselves haven't changed.
  }, [
    globalDispatch,
    listId,
    name,
    email,
    sortKey,
    sortAscending,
    page,
    pageSize,
    searchToken,
    readCount,
  ]);

  const refresh = useCallback(() => setReadCount((count) => count + 1), []);

  return { members, total, loading, refresh };
}
